import React, { useState, useEffect } from 'react';
import { Video, PlusCircle, Trash2, Save, Search, Edit, X, Play } from 'lucide-react';
import { api } from '../services/apiClient';

const VideoManager = () => {
  const [videos, setVideos] = useState([]);
  const [isAdding, setIsAdding] = useState(false);
  const [newVideo, setNewVideo] = useState({ name: '', description: '', video_url: '', priority: 0, visible: false });
  const [editingId, setEditingId] = useState(null);
  const [editVideo, setEditVideo] = useState({ name: '', description: '', video_url: '', priority: 0, visible: false });
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [previewVideo, setPreviewVideo] = useState(null);

  function extractVideoId(url) {
    if (!url) return null;

    // Prova vari formati di YouTube
    const patterns = [
      /(?:youtube\.com\/watch\?v=)([^&\n?#]+)/,     // youtube.com/watch?v=ID
      /(?:youtu\.be\/)([^&\n?#]+)/,                 // youtu.be/ID
      /(?:youtube\.com\/embed\/)([^&\n?#]+)/,       // youtube.com/embed/ID
      /(?:youtube\.com\/shorts\/)([^&\n?#]+)/,      // youtube.com/shorts/ID (Shorts)
      /^([a-zA-Z0-9_-]{11})$/, // Solo ID
    ];

    for (let pattern of patterns) {
      const match = url.match(pattern);
      if (match && match[1]) {
        return match[1];
      }
    }
    return null;
  }

  function getEmbedUrl(url) {
    const videoId = extractVideoId(url);
    if (!videoId) {
      console.warn('Could not extract video ID from:', url);
      return null;
    }
    return `https://www.youtube.com/embed/${videoId}?rel=0`;
  }

  useEffect(() => {
    fetchVideos();
  }, []);

  async function fetchVideos() {
    try {
      const data = await api.videos.list();
      setVideos(data);
    } catch (err) {
      console.error('Errore caricamento video:', err);
    }
  }

  async function handleAddVideo(e) {
    e.preventDefault();
    if (!newVideo.name || !newVideo.video_url) return;

    try {
      const created = await api.videos.create({
        name: newVideo.name,
        description: newVideo.description,
        video_url: newVideo.video_url,
        priority: parseInt(newVideo.priority) || 0,
        visible: newVideo.visible,
      });
      setVideos([...videos, created].sort((a, b) => a.priority - b.priority));
      setIsAdding(false);
      setNewVideo({ name: '', description: '', video_url: '', priority: 0, visible: false });
    } catch (err) {
      alert(`Errore: ${err.message}`);
    }
  }

  async function handleUpdateVideo(id) {
    if (!editVideo.name || !editVideo.video_url) return;

    try {
      await api.videos.update(id, {
        name: editVideo.name,
        description: editVideo.description,
        video_url: editVideo.video_url,
        priority: parseInt(editVideo.priority) || 0,
        visible: editVideo.visible,
      });
      setVideos(videos.map(v => v.id === id ? { ...v, ...editVideo, priority: parseInt(editVideo.priority) || 0 } : v).sort((a, b) => a.priority - b.priority));
      setEditingId(null);
    } catch (err) {
      alert(`Errore: ${err.message}`);
    }
  }

  async function handleDeleteVideo() {
    if (!deleteConfirm) return;

    try {
      await api.videos.delete(deleteConfirm.id);
      setVideos(videos.filter(v => v.id !== deleteConfirm.id));
      setDeleteConfirm(null);
    } catch (err) {
      alert(`Errore: ${err.message}`);
    }
  }

  const filteredVideos = videos.filter(v =>
    v.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    v.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-2 text-brand-dark">
          <Video size={24} />
          <h3 className="text-xl font-semibold">Gestione Video</h3>
        </div>
        {!isAdding && (
          <button
            onClick={() => setIsAdding(true)}
            className="flex items-center gap-2 bg-brand-dark text-white px-4 py-2 rounded-lg hover:bg-opacity-90 transition-all"
          >
            <PlusCircle size={18} /> Aggiungi Video
          </button>
        )}
      </div>

      {isAdding && (
        <div className="bg-white p-6 rounded-xl border border-brand-main shadow-sm max-w-2xl mx-auto">
          <form onSubmit={handleAddVideo} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">Nome Video</label>
              <input
                className="w-full p-2 border border-gray-200 rounded-md focus:ring-2 focus:ring-brand-main outline-none"
                placeholder="Nome del video"
                value={newVideo.name}
                onChange={e => setNewVideo({ ...newVideo, name: e.target.value })}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">Descrizione</label>
              <textarea
                className="w-full p-2 border border-gray-200 rounded-md focus:ring-2 focus:ring-brand-main outline-none"
                placeholder="Breve descrizione del video"
                rows="2"
                value={newVideo.description}
                onChange={e => setNewVideo({ ...newVideo, description: e.target.value })}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">Link Video (YouTube)</label>
              <input
                type="url"
                className="w-full p-2 border border-gray-200 rounded-md focus:ring-2 focus:ring-brand-main outline-none"
                placeholder="https://youtube.com/watch?v=... o https://youtu.be/..."
                value={newVideo.video_url}
                onChange={e => setNewVideo({ ...newVideo, video_url: e.target.value })}
                required
              />
              {newVideo.video_url && getEmbedUrl(newVideo.video_url) && (
                <div className="mt-3 bg-black rounded-lg overflow-hidden">
                  <div style={{ position: 'relative', width: '100%', paddingBottom: '56.25%', height: 0 }}>
                    <iframe
                      src={getEmbedUrl(newVideo.video_url)}
                      title="Anteprima video"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                      allowFullScreen
                      style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
                    ></iframe>
                  </div>
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">Priorità (1 = primo)</label>
                <input
                  type="number"
                  className="w-full p-2 border border-gray-200 rounded-md focus:ring-2 focus:ring-brand-main outline-none"
                  value={newVideo.priority}
                  onChange={e => setNewVideo({ ...newVideo, priority: e.target.value })}
                />
              </div>

              <div className="flex items-end">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={newVideo.visible}
                    onChange={e => setNewVideo({ ...newVideo, visible: e.target.checked })}
                    className="w-4 h-4 rounded"
                  />
                  <span className="text-sm font-medium text-gray-600">Visibile</span>
                </label>
              </div>
            </div>

            <div className="flex gap-2 justify-end pt-2">
              <button type="submit" className="bg-brand-dark text-white px-4 py-2 rounded-lg hover:bg-opacity-90">
                Salva
              </button>
              <button type="button" onClick={() => { setIsAdding(false); setNewVideo({ name: '', description: '', video_url: '', priority: 0, visible: false }); }} className="px-4 py-2 text-gray-500">
                Annulla
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="flex justify-center mb-6">
        <div className="relative w-full max-w-md">
          <Search className="absolute left-3 top-2.5 text-gray-400" size={18} />
          <input
            type="text"
            placeholder="Cerca video..."
            className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg outline-none focus:ring-2 focus:ring-brand-main"
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      <div className="space-y-3">
        {filteredVideos.length === 0 ? (
          <div className="text-center py-10 text-gray-400 italic">
            Nessun video trovato.
          </div>
        ) : (
          filteredVideos.map(video => (
            <div key={video.id} className="bg-white p-4 rounded-xl border border-brand-main shadow-sm group flex gap-4">
              {/* Thumbnail with Play Button */}
              <div className="w-32 h-24 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0 border border-gray-200 relative cursor-pointer">
                <img
                  src={(() => {
                    const youtubeRegex = /(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\n?#]+)/;
                    const match = video.video_url.match(youtubeRegex);
                    if (match && match[1]) {
                      return `https://img.youtube.com/vi/${match[1]}/hqdefault.jpg`;
                    }
                    return 'https://via.placeholder.com/128x96?text=Video';
                  })()}
                  alt={video.name}
                  className="w-full h-full object-cover"
                />
                <button
                  type="button"
                  onClick={() => setPreviewVideo(video)}
                  className="absolute inset-0 bg-black/30 hover:bg-black/50 transition-all flex items-center justify-center"
                >
                  <Play size={32} className="text-white fill-white" />
                </button>
              </div>

              {/* Content */}
              {editingId === video.id ? (
                <div className="flex-1 space-y-2">
                  <input
                    className="w-full p-1 border rounded outline-none focus:ring-1 focus:ring-brand-main text-sm"
                    value={editVideo.name}
                    onChange={e => setEditVideo({ ...editVideo, name: e.target.value })}
                    required
                  />
                  <textarea
                    className="w-full p-1 border rounded outline-none focus:ring-1 focus:ring-brand-main text-xs"
                    rows="2"
                    value={editVideo.description}
                    onChange={e => setEditVideo({ ...editVideo, description: e.target.value })}
                  />
                  <input
                    type="url"
                    className="w-full p-1 border rounded outline-none focus:ring-1 focus:ring-brand-main text-xs"
                    value={editVideo.video_url}
                    onChange={e => setEditVideo({ ...editVideo, video_url: e.target.value })}
                    required
                  />
                  <div className="flex gap-2 items-center">
                    <input
                      type="number"
                      className="w-16 p-1 border rounded outline-none focus:ring-1 focus:ring-brand-main text-xs"
                      value={editVideo.priority}
                      onChange={e => setEditVideo({ ...editVideo, priority: e.target.value })}
                    />
                    <label className="flex items-center gap-1 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={editVideo.visible}
                        onChange={e => setEditVideo({ ...editVideo, visible: e.target.checked })}
                        className="w-4 h-4 rounded"
                      />
                      <span className="text-xs">Visibile</span>
                    </label>
                  </div>
                  <div className="flex gap-1 justify-end pt-2">
                    <button onClick={() => handleUpdateVideo(video.id)} className="text-green-600 p-1"><Save size={16}/></button>
                    <button onClick={() => setEditingId(null)} className="text-gray-400 p-1"><X size={16}/></button>
                  </div>
                </div>
              ) : (
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1">
                      <p className="font-semibold text-brand-dark">{video.name}</p>
                      <p className="text-sm text-brand-muted mt-1">{video.description}</p>
                      <div className="flex gap-3 mt-2 text-xs text-gray-500">
                        <span>Priorità: {video.priority}</span>
                        <span>{video.visible ? '✓ Visibile' : '✗ Nascosto'}</span>
                        <span>{new Date(video.created_at).toLocaleDateString('it-IT')}</span>
                      </div>
                    </div>
                    <div className="flex gap-2 flex-shrink-0">
                      <button
                        onClick={() => { setEditingId(video.id); setEditVideo(video); }}
                        className="text-blue-500 hover:text-blue-700 p-1"
                      >
                        <Edit size={16} />
                      </button>
                      <button
                        onClick={() => setDeleteConfirm(video)}
                        className="text-red-400 hover:text-red-600 p-1"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-lg max-w-sm w-full p-6 space-y-4">
            <h3 className="text-lg font-semibold text-brand-dark">Elimina Video</h3>
            <p className="text-gray-600">Sei sicuro di voler eliminare "{deleteConfirm.name}"?</p>
            <div className="flex gap-2 justify-end pt-4">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Annulla
              </button>
              <button
                onClick={handleDeleteVideo}
                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
              >
                Elimina
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Video Preview Modal */}
      {previewVideo && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <div className="bg-black rounded-xl shadow-lg max-w-4xl w-full space-y-4">
            <div className="flex justify-between items-center p-4 border-b border-gray-700">
              <h3 className="text-lg font-semibold text-white">{previewVideo.name}</h3>
              <button
                onClick={() => setPreviewVideo(null)}
                className="p-1 hover:bg-gray-700 rounded-full transition-colors"
              >
                <X size={24} className="text-white" />
              </button>
            </div>
            <div style={{ position: 'relative', width: '100%', paddingBottom: '56.25%', height: 0 }}>
              <iframe
                src={getEmbedUrl(previewVideo.video_url)}
                title={previewVideo.name}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                allowFullScreen
                style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
              ></iframe>
            </div>
            <div className="p-4 border-t border-gray-700">
              <p className="text-gray-300 text-sm">{previewVideo.description}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VideoManager;
